import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Apple, Play, AlertTriangle, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AppDownloadModal, useDownloadModal, trackPageView } from "./AppDownloadModal";

const LOGO = "https://customer-assets.emergentagent.com/job_tripsi-launch/artifacts/7y1dc8ot_full_logo_red.png";

export function LanguageToggle() {
  const { i18n } = useTranslation();
  const current = i18n.language;
  const toggle = () => {
    const next = current === "en" ? "fr" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("tripsi_lang", next);
  };
  return (
    <button onClick={toggle} data-testid="language-toggle" className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-medium transition-colors px-2 py-1 border border-white/10 hover:border-white/20" title={current === "en" ? "Passer au français" : "Switch to English"}>
      <Globe size={14} />
      <span className="uppercase">{current === "en" ? "FR" : "EN"}</span>
    </button>
  );
}

export function ServiceBanner() {
  const { t } = useTranslation();
  return (
    <div data-testid="service-banner" className="bg-[#E60000] text-white sticky top-0 z-[60] py-2.5 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
        <AlertTriangle size={14} className="shrink-0" />
        <p className="text-xs md:text-sm font-medium">
          <strong>{t("serviceBanner.notice")}</strong> {t("serviceBanner.text")}{" "}
          <a href="tel:+16472499977" className="underline font-bold">+1 647-249-9977</a>.
        </p>
      </div>
    </div>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/riders", label: t("nav.riders") },
    { to: "/drivers", label: t("nav.drivers") },
    { to: "/businesses", label: t("nav.businesses") },
    { to: "/contact", label: t("nav.contact") },
  ];
  const isActive = (p) => location.pathname === p;

  return (
    <nav data-testid="navbar" className="sticky top-[40px] z-50 bg-[#0A0A0A]/80 backdrop-blur-2xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between py-3">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
          <img src={LOGO} alt="Tripsi" className="h-20 md:h-[5.5rem] w-auto object-contain" />
          <span className="text-[10px] text-[#A3A3A3] tracking-wider whitespace-nowrap border-l border-white/10 pl-3">{t("nav.madeInCanada")}</span>
        </Link>
        <div className="hidden lg:flex items-center gap-7">
          {links.map(l => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className={`text-sm font-medium transition-colors ${isActive(l.to) ? "text-[#E60000]" : "text-white/60 hover:text-white"}`}>{l.label}</Link>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          <a href="tel:+16472499977" data-testid="nav-call-dispatch" className="flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3B30] text-white px-5 py-2.5 text-sm font-semibold transition-colors"><Phone size={15} />{t("nav.callDispatch")}</a>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2">{open ? <X size={24} /> : <Menu size={24} />}</button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-[#0A0A0A] border-b border-white/5 overflow-hidden">
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map(l => (<Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`text-base font-medium ${isActive(l.to) ? "text-[#E60000]" : "text-white/60"}`}>{l.label}</Link>))}
              <div className="flex items-center gap-3 mt-2">
                <LanguageToggle />
              </div>
              <a href="tel:+16472499977" className="flex items-center gap-2 bg-[#E60000] text-white px-5 py-3 text-sm font-semibold justify-center mt-2"><Phone size={15} />{t("nav.callDispatch")}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export function Footer({ onDownload }) {
  const { t } = useTranslation();
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] border-t border-white/5 py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <img src={LOGO} alt="Tripsi" className="h-[5.5rem] md:h-24 w-auto" />
            <p className="text-[#A3A3A3] mt-4 text-sm leading-relaxed max-w-md">{t("footer.description")}</p>
            <div className="flex gap-3 mt-6">
              <button onClick={onDownload} data-testid="footer-app-store" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/5 px-4 py-2.5 text-sm text-white transition-colors"><Apple size={16} />App Store</button>
              <button onClick={onDownload} data-testid="footer-google-play" className="flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/5 px-4 py-2.5 text-sm text-white transition-colors"><Play size={16} />Google Play</button>
            </div>
          </div>
          <div>
            <h4 className="font-['Outfit'] font-bold text-white mb-4 text-xs uppercase tracking-widest">{t("footer.navigation")}</h4>
            <div className="flex flex-col gap-3">
              {[{to:"/",l:t("nav.home")},{to:"/riders",l:t("nav.riders")},{to:"/drivers",l:t("nav.drivers")},{to:"/businesses",l:t("nav.businesses")},{to:"/contact",l:t("footer.contactFaq")}].map(n=>(<Link key={n.to} to={n.to} className="text-[#A3A3A3] hover:text-white text-sm transition-colors">{n.l}</Link>))}
            </div>
          </div>
          <div>
            <h4 className="font-['Outfit'] font-bold text-white mb-4 text-xs uppercase tracking-widest">{t("footer.legal")}</h4>
            <div className="flex flex-col gap-3">
              <Link to="/privacy" className="text-[#A3A3A3] hover:text-white text-sm transition-colors">{t("footer.privacyPolicy")}</Link>
              <Link to="/terms" className="text-[#A3A3A3] hover:text-white text-sm transition-colors">{t("footer.termsConditions")}</Link>
            </div>
            <h4 className="font-['Outfit'] font-bold text-white mb-4 mt-8 text-xs uppercase tracking-widest">{t("footer.dispatch")}</h4>
            <a href="tel:+16472499977" className="text-[#E60000] hover:text-[#FF3B30] text-sm font-semibold transition-colors">+1 647-249-9977</a>
          </div>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-[#E60000]/20 to-transparent mb-8" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#A3A3A3] text-xs">&copy; {new Date().getFullYear()} Tripsi. {t("footer.rights")}</p>
          <p className="text-[#A3A3A3] text-xs">{t("nav.madeInCanada")}</p>
        </div>
      </div>
    </footer>
  );
}

export function StickyDispatchButton() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const h = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.a href="tel:+16472499977" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} data-testid="sticky-call-dispatch" className="fixed bottom-6 right-4 z-50 md:right-6 flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3B30] text-white px-5 py-3 font-semibold text-sm shadow-lg shadow-red-900/30 transition-colors">
          <Phone size={16} /><span className="hidden sm:inline">{t("nav.callDispatch")}</span><span className="sm:hidden">Call</span>
        </motion.a>
      )}
    </AnimatePresence>
  );
}

export default function Layout({ children }) {
  const { open, openModal, closeModal } = useDownloadModal();
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col">
      <ServiceBanner />
      <Navbar />
      <main className="flex-1">{typeof children === "function" ? children({ onDownload: openModal }) : children}</main>
      <Footer onDownload={openModal} />
      <StickyDispatchButton />
      <AppDownloadModal open={open} onClose={closeModal} />
    </div>
  );
}
